import React from 'react';
import Button from './Button';
import StatusPill from './StatusPill';
import { StarsText } from './Stars';
import { decimalFr, reviewCountFr, distanceLabel, localpageHref } from './format';

/**
 * `.salon-row` — carte d'un salon dans la liste.
 *
 * Corrections portées depuis la maquette :
 *  - piège n°4 : seule la première carte liait vers le détail, les cinq autres
 *    étaient des `<button>` inertes. « En savoir + » est toujours un lien ;
 *  - piège n°3 : séparateur décimal en virgule ;
 *  - §7.4 : « Appeler le salon » n'avait aucun `tel:` — le bouton disparaît
 *    quand le salon n'a pas de téléphone renseigné, plutôt que de mentir ;
 *  - décision 9 : le libellé de distance dépend du point de référence, et la
 *    puce disparaît quand il n'y en a aucun.
 *
 * La carte reste présentationnelle : le clic téléphone est remonté via
 * `onPhoneClick`, c'est `LocatorLayout` qui sait à quel salon il appartient et
 * qui le piste.
 *
 * Les microdonnées `itemProp` reprennent la convention de 2025_VOG : le listing
 * est un ItemList de LocalBusiness (plan §13 Phase 3).
 */
const SalonRow = ({
  name,
  nameLines,
  photo,
  photoAlt,
  rating,
  reviewCount,
  status,
  address,
  distanceKm,
  distanceOrigin,
  phone,
  onPhoneClick,
  localpagePath,
  position,
}) => {
  const distance = distanceLabel(distanceKm, distanceOrigin);
  const lines = nameLines && nameLines.length ? nameLines : [name];

  return (
    <article className="salon-row" itemProp="itemListElement" itemScope itemType="https://schema.org/HairSalon">
      {position != null && <meta itemProp="position" content={position} />}

      <div className="salon-row-photo">
        <img src={photo} alt={photoAlt || name} width="96" height="96" loading="lazy" />
      </div>

      <div className="salon-row-body">
        <div className="salon-row-top">
          <h2 itemProp="name">
            {lines.map((line, index) => (
              <React.Fragment key={line}>
                {index > 0 && <br />}
                {line}
              </React.Fragment>
            ))}
          </h2>

          {rating > 0 && (
            <div
              className="salon-row-rating"
              itemProp="aggregateRating"
              itemScope
              itemType="https://schema.org/AggregateRating"
            >
              <meta itemProp="ratingValue" content={rating} />
              <meta itemProp="bestRating" content="5" />
              {reviewCount > 0 && <meta itemProp="reviewCount" content={reviewCount} />}
              <StarsText rating={rating} />
              <span className="score">{decimalFr(rating)}/5</span>
              {reviewCount > 0 && <span className="count">{reviewCountFr(reviewCount)}</span>}
            </div>
          )}
        </div>

        {status && status.label && <StatusPill isOpen={status.isOpen} label={status.label} title={status.title} />}

        <div className="salon-row-bottom">
          <p className="salon-row-addr" itemProp="address">
            {address}
          </p>
          {distance && <span className="salon-row-dist">{distance}</span>}
        </div>

        <div className="salon-row-cta">
          {phone && (
            <Button variant="yellow" href={`tel:${phone.replace(/\s/g, '')}`} onClick={onPhoneClick}>
              Appeler le salon
            </Button>
          )}
          {localpagePath && (
            <a className="btn white" href={localpageHref(localpagePath, distanceOrigin)} itemProp="url">
              En savoir + <span className="sr-only">sur {name}</span>
            </a>
          )}
        </div>
      </div>
    </article>
  );
};

export default SalonRow;
